import { FC, useRef, useEffect, useState, useCallback } from "react";
import { Box, Paper, Typography, Chip, IconButton } from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { FixedPanel, FixedPanelRef } from "../../components/FixedPanel";
import { MessageContent } from "./MessageContent";
import { parseQuestionsAndCleanContent } from "./questionParser";
import { ConciergeHandoffCard } from "./ConciergeHandoffCard";
import { extractConciergeHandoff, hasConciergeHandoff } from "./conciergeHandoff";

/** Message type that works for both live chat and admin views */
export interface MessageItem {
  id?: number;
  role: string;
  content: string;
  createdAt?: Date | string;
}

interface MessageListProps {
  messages: MessageItem[];
  viewMode?: "markdown" | "rich";
  /** When true, renders without FixedPanel wrapper (for use in admin modals) */
  standalone?: boolean;
  /** Callback for inline feedback on the last assistant message */
  onFeedback?: (rating: number) => void;
}

export const MessageList: FC<MessageListProps> = ({
  messages,
  viewMode = "rich",
  standalone = false,
  onFeedback,
}) => {
  const panelRef = useRef<FixedPanelRef>(null);
  const feedbackRef = useRef<HTMLDivElement>(null);
  const [feedbackGiven, setFeedbackGiven] = useState<number | null>(null);

  // Reset feedback state when conversation changes
  useEffect(() => {
    setFeedbackGiven(null);
  }, [messages.length]);

  const handleFeedback = useCallback(
    (rating: number) => {
      setFeedbackGiven(rating);
      onFeedback?.(rating);
    },
    [onFeedback]
  );

  // Show feedback bubble only when last message is from assistant
  const showFeedbackBubble =
    !standalone &&
    onFeedback &&
    messages.length > 0 &&
    messages[messages.length - 1].role === "assistant";

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (!standalone && messages.length > 0) {
      const timer = setTimeout(() => {
        panelRef.current?.scrollToBottom();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [messages.length, standalone]);

  // One card, on the latest Concierge offer: repeated cards read as wallpaper (#6903 got 9).
  // Earlier offers keep the inline link.
  const lastHandoffIndex = messages.reduce(
    (last, m, i) => (m.role === "assistant" && hasConciergeHandoff(m.content) ? i : last),
    -1,
  );

  const content = (
    <Box
      sx={{
        p: 2,
        backgroundColor: "#ffffff",
        minHeight: "100%",
      }}
    >
      {messages.map((message, index) => (
        <Box key={message.id ?? index}>
          <MessageBubble
            message={message}
            viewMode={viewMode}
            readOnly={standalone}
            showHandoffCard={index === lastHandoffIndex}
          />
        </Box>
      ))}

      {/* Centered feedback mini-bubble after last assistant reply */}
      {showFeedbackBubble && (
        <Box ref={feedbackRef} sx={{ display: "flex", justifyContent: "center", my: 1 }}>
          {feedbackGiven != null ? (
            <Typography
              variant="caption"
              sx={{ color: "text.disabled", fontSize: "0.7rem" }}
            >
              {feedbackGiven > 0 ? "👍 Thanks!" : "Thanks for feedback"}
            </Typography>
          ) : (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.5,
                borderRadius: 3,
                bgcolor: "#f5f5f5",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <IconButton
                size="small"
                onClick={() => handleFeedback(1)}
                sx={{
                  p: 0.25,
                  color: "success.main",
                  opacity: 0.7,
                  "&:hover": { opacity: 1, bgcolor: "success.lighter" },
                }}
              >
                <ThumbUpIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography
                variant="caption"
                sx={{ color: "text.disabled", fontSize: "0.7rem", userSelect: "none" }}
              >
                Helpful?
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleFeedback(-1)}
                sx={{
                  p: 0.25,
                  color: "error.main",
                  opacity: 0.7,
                  "&:hover": { opacity: 1, bgcolor: "error.lighter" },
                }}
              >
                <ThumbDownIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  // In standalone mode, just render the content without FixedPanel wrapper
  if (standalone) {
    return content;
  }

  return (
    <FixedPanel ref={panelRef} margin={0}>
      {content}
    </FixedPanel>
  );
};

interface MessageBubbleProps {
  message: MessageItem;
  viewMode: "markdown" | "rich";
  /** Admin modal: the handoff card renders but does not navigate */
  readOnly?: boolean;
  /** Only the latest Concierge offer in the thread becomes a card */
  showHandoffCard?: boolean;
}

const MessageBubble: FC<MessageBubbleProps> = ({
  message,
  viewMode,
  readOnly = false,
  showHandoffCard = false,
}) => {
  const isUser = message.role === "user";

  const { questions, cleanedContent: parsedContent } = isUser
    ? { questions: [], cleanedContent: message.content }
    : viewMode === "markdown"
      ? { questions: [], cleanedContent: message.content } // In markdown mode, show raw content
      : parseQuestionsAndCleanContent(message.content);

  const handoff =
    showHandoffCard && !isUser && viewMode === "rich"
      ? extractConciergeHandoff(parsedContent)
      : { content: parsedContent, actionKey: null };
  const cleanedContent = handoff.content;

  // Format timestamp - handle both Date objects and strings
  const formatTimestamp = (timestamp?: Date | string) => {
    if (!timestamp) return null;
    const date =
      typeof timestamp === "string" ? new Date(timestamp + "Z") : timestamp;
    return date.toLocaleTimeString();
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        mb: 1.5,
      }}
    >
      <Paper
        elevation={1}
        sx={{
          maxWidth: isUser ? "80%" : "90%",
          p: 1.5,
          bgcolor: isUser ? "primary.main" : "#f5f5f5",
          color: isUser ? "primary.contrastText" : "text.primary",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: isUser ? 0.5 : 2,
          }}
        >
          <Chip
            label={isUser ? "You" : "AI"}
            size="small"
            color={isUser ? "primary" : "default"}
            sx={{
              height: 20,
              fontSize: isUser ? "0.7rem" : "0.75rem",
              "& .MuiChip-label": { px: 1, py: 0 },
            }}
          />
          {message.createdAt && (
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.7rem",
                color: isUser ? "rgba(255, 255, 255, 0.8)" : "text.secondary",
                fontWeight: isUser ? 500 : 400,
              }}
            >
              {formatTimestamp(message.createdAt)}
            </Typography>
          )}
        </Box>
        <Box>
          {viewMode === "markdown" ? (
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {cleanedContent}
            </Typography>
          ) : (
            <MessageContent content={cleanedContent} isUser={isUser} />
          )}
        </Box>

        {handoff.actionKey && (
          <ConciergeHandoffCard actionKey={handoff.actionKey} preview={readOnly} />
        )}

        {viewMode === "rich" && questions.length > 0 && (
          <Box
            sx={{
              mt: 1.5,
              p: 1,
              bgcolor: "info.lighter",
              borderLeft: "3px solid",
              borderColor: "info.main",
              borderRadius: 1,
              backgroundColor: "#fafafa",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "info.main",
                display: "block",
                mb: 0.5,
              }}
            >
              Questions to consider:
            </Typography>
            {questions.map((q, idx) => (
              <Box
                key={idx}
                sx={{
                  fontSize: "0.8125rem",
                  color: "text.secondary",
                  mb: 0.5,
                  "& p": { m: 0 },
                  "& strong": { fontWeight: 600 },
                }}
              >
                <MessageContent content={`• ${q}`} isUser={false} />
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};
